import { useState } from "react";
import { CheckCircle2, Scale } from "lucide-react";

const MODULE_LABEL = {
  gap: "DPDP Gap",
  pia: "PIA",
  ropa: "ROPA",
  tprm: "TPRM",
};

const MODULE_STYLE = {
  gap: "bg-sky-50 text-sky-700",
  pia: "bg-violet-50 text-violet-700",
  ropa: "bg-amber-50 text-amber-800",
  tprm: "bg-emerald-50 text-emerald-700",
};

export default function ChecklistItemDetails({ item, index, itemKey, checked = false, onToggle, matches = [] }) {
  const [showAllMatches, setShowAllMatches] = useState(false);
  const visibleMatches = showAllMatches ? matches : matches.slice(0, 2);
  const extras = [
    item.evidence && ["Evidence", item.evidence],
    item.owner && ["Owner", item.owner],
    item.priority && ["Priority", item.priority],
    item.status && ["Status", item.status],
    item.notes && ["Notes", item.notes],
  ].filter(Boolean);
  const key = itemKey || `${index}:${item.id || item.code || "item"}`;

  return (
    <div className={`flex items-start gap-3 px-5 py-4 sm:px-6 ${checked ? "bg-emerald-50/40" : ""}`}>
      {onToggle && (
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onToggle(key);
          }}
          aria-pressed={checked}
          aria-label={checked ? "Mark requirement incomplete" : "Mark requirement complete"}
          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border transition-colors ${
            checked
              ? "border-emerald-500 bg-emerald-500 text-white"
              : "border-slate-300 bg-white text-transparent hover:border-blue-400"
          }`}
        >
          <CheckCircle2 className="h-4 w-4" />
        </button>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold text-slate-400">
            {item.code || String(index + 1).padStart(2, "0")}
          </span>
          {item.category && String(item.category).toLowerCase() !== "general" && (
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">
              {item.category}
            </span>
          )}
          {item.regulation && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
              {item.regulation}
            </span>
          )}
          {item.source === "tenant" && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">
              Tenant
            </span>
          )}
        </div>
        <p className={`mt-2 text-sm font-semibold leading-6 ${checked ? "text-slate-500 line-through" : "text-slate-800"}`}>
          {item.title}
        </p>
        {extras.length > 0 && (
          <dl className="mt-3 space-y-1.5">
            {extras.map(([label, value]) => (
              <div key={label} className="text-xs leading-5 text-slate-500">
                <span className="font-bold text-slate-600">{label}: </span>
                {value}
              </div>
            ))}
          </dl>
        )}
        {matches.length > 0 && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
              <Scale className="h-3.5 w-3.5 text-blue-600" />
              Matching law
            </div>
            <ul className="mt-2 space-y-2">
              {visibleMatches.map((question) => (
                <li key={`${question.module}-${question.id}`} className="text-xs leading-5 text-slate-700">
                  <span className={`mr-1.5 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-bold ${MODULE_STYLE[question.module] || "bg-slate-100 text-slate-600"}`}>
                    {MODULE_LABEL[question.module] || question.module}
                  </span>
                  {question.ref && <span className="mr-1.5 font-semibold text-blue-700">{question.ref}</span>}
                  {question.title}
                </li>
              ))}
            </ul>
            {matches.length > 2 && (
              <button
                type="button"
                onClick={() => setShowAllMatches((current) => !current)}
                className="mt-2 text-[11px] font-bold text-blue-700"
              >
                {showAllMatches ? "Show less" : `Show all ${matches.length}`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
